
import { ref, reactive, onMounted, watch, nextTick, computed } from 'vue';

interface CskEntry {
  id?: number;
  externalId: string;
  codeEDRPOU?: string | null;
  sortOrder: number;
  isHidden: boolean;
  data?: any;
  // локальные редактируемые
  localSortOrder: number;
  localHidden: boolean;
  originalSortOrder: number;
  originalHidden: boolean;
  // отображение/состояние
  primaryName?: string;
  secondaryNames?: string[];
  saving?: boolean;
  saveMessage?: string;
  saveError?: string;
}

export default {
  name: 'WidgetCreator',
  setup() {
    const activeTab = ref('config');
    const statusMessage = ref('');
    const statusType = ref('info');
    const generatedConfig = ref(null);
    const widgetPreview = ref(null);
    const generatedCode = ref(null);
    const generatedCodeText = ref('// Configure your widget and click "Generate Widget" to see the code here.');

    const form = reactive({
      creationToken: '',
      usageToken: '',
        widgetInstanceId: null as number | null,
      displayType: 'modal',
      embeddingBlock: '',
      language: 'en',
      callbackUrl: '',
      fileLimit: null,
      sendUnsignedFiles: false,
      showFileUpload: true,
      enableSessionPersistence: false,
      requireClientKey: false,
      colors: {
        primary: '#007bff',
        secondary: '#6c757d',
        background: '#ffffff',
        text: '#212529'
      }
    });

    const cskItems = ref<CskEntry[]>([]);
    const cskLoading = ref(false);
    const cskError = ref('');
    const bulkSaving = ref(false);

    const colorTexts = reactive({
      primary: '#007bff',
      secondary: '#6c757d',
      background: '#ffffff',
      text: '#212529'
    });

    const addStatus = (message, type = 'info') => {
      statusMessage.value = message;
      statusType.value = type;
      setTimeout(() => {
        statusMessage.value = '';
      }, 5000);
    };

    const updateColorText = (color: string) => {
      colorTexts[color] = form.colors[color];
    };

    const updateColorPicker = (color: string) => {
      if (/^#[0-9A-F]{6}$/i.test(colorTexts[color])) {
        form.colors[color] = colorTexts[color];
      }
    };

    const toggleEmbeddingBlock = () => {
      // This is handled by v-show in the template
    };

    const enrichCsk = (raw: any): CskEntry => {
      const data = raw?.data ?? {};
      const issuerCNs: string[] = Array.isArray(data.issuerCNs) ? data.issuerCNs : [];
      const primaryName = (issuerCNs[0] ?? data.name ?? 'Unknown CSK') as string;
      const secondaryNames: string[] = issuerCNs.length > 1 ? issuerCNs.slice(1) : [];

      return {
        id: raw?.id,
        externalId: raw?.externalId ?? String(raw?.id ?? ''),
        codeEDRPOU: raw?.codeEDRPOU ?? null,
        sortOrder: Number.isFinite(raw?.sortOrder) ? raw.sortOrder : 0,
        isHidden: Boolean(raw?.isHidden),
        data,
        localSortOrder: Number.isFinite(raw?.sortOrder) ? raw.sortOrder : 0,
        localHidden: Boolean(raw?.isHidden),
        originalSortOrder: Number.isFinite(raw?.sortOrder) ? raw.sortOrder : 0,
        originalHidden: Boolean(raw?.isHidden),
        primaryName,
        secondaryNames,
        saving: false,
        saveMessage: '',
        saveError: ''
      };
    };

    const applyCskItems = (items: any[]) => {
      if (!Array.isArray(items)) {
        cskItems.value = [];
        return;
      }
      cskItems.value = items.filter(Boolean).map(enrichCsk);
    };

    const resetCskEntry = (entry: CskEntry) => {
      entry.localSortOrder = entry.originalSortOrder;
      entry.localHidden = entry.originalHidden;
      entry.saveMessage = '';
      entry.saveError = '';
    };

    const resetAllCsk = () => {
      cskItems.value.forEach(resetCskEntry);
      addStatus('All CSK changes have been reset.', 'info');
    };

    const cskItemsSorted = computed(() => {
      const rank = (x: any) => ((x?.localHidden ?? x?.isHidden) ? 1 : 0); // 0 = visible, 1 = hidden
      const num  = (v: any) => (Number.isFinite(v) ? Number(v) : 0);

      return (Array.isArray(cskItems.value) ? cskItems.value : [])
          .filter(Boolean)
          .slice() // не мутируем оригинал
          .sort((a, b) => {
            // 1) видимость: видимые раньше скрытых
            const byVisibility = rank(a) - rank(b);
            if (byVisibility !== 0) return byVisibility;

            // 2) сортировка по цифре
            const byOrder = num(a?.localSortOrder ?? a?.sortOrder) - num(b?.localSortOrder ?? b?.sortOrder);
            if (byOrder !== 0) return byOrder;

            // 3) стабильный тай-брейк
            const nameA = (a?.primaryName ?? '').toString();
            const nameB = (b?.primaryName ?? '').toString();
            const byName = nameA.localeCompare(nameB);
            if (byName !== 0) return byName;

            return (a?.id ?? 0) - (b?.id ?? 0);
          });
    });


    const validateForm = async () => {
      if (!form.creationToken) {
        addStatus('Widget Creation Token is required', 'error');
        return false;
      }

      if (!form.usageToken) {
        addStatus('Widget Usage Token is required', 'error');
        return false;
      }

      return true;
    };

    const loadConfigFromCreationToken = async () => {
      if (!form.creationToken) {
        cskItems.value = [];
        cskError.value = '';
        return;
      }

      cskLoading.value = true;
      cskError.value = '';

      try {
        const serverUrl = process.env.VUE_APP_SERVER_LINK || 'http://localhost:3001';
        const response = await fetch(serverUrl + '/widget/creation-config/' + form.creationToken);

        if (response.ok) {
          const data = await response.json();

          if (data.config) {
            form.widgetInstanceId = data.config.widgetInstanceId ?? null;

            if (data.config.displayType) form.displayType = data.config.displayType;
            if (data.config.language) form.language = data.config.language;
            if (data.config.callbackUrl) form.callbackUrl = data.config.callbackUrl;
            if (data.config.sendUnsignedFiles !== undefined) form.sendUnsignedFiles = data.config.sendUnsignedFiles;
            if (data.config.showFileUpload !== undefined) form.showFileUpload = data.config.showFileUpload;
            if (data.config.embeddingBlock) form.embeddingBlock = data.config.embeddingBlock;
            if (data.config.enableSessionPersistence !== undefined) form.enableSessionPersistence = data.config.enableSessionPersistence;
            if (data.config.fileLimit !== undefined) form.fileLimit = data.config.fileLimit;
            if (data.config.requireClientKey !== undefined) form.requireClientKey = data.config.requireClientKey;

            if (data.config.colors) {
              let colors = data.config.colors;
              if (typeof colors === 'string') {
                try {
                  colors = JSON.parse(colors);
                } catch {
                  colors = {};
                }
              }
              if (colors.primary) {
                form.colors.primary = colors.primary;
                colorTexts.primary = colors.primary;
              }
              if (colors.secondary) {
                form.colors.secondary = colors.secondary;
                colorTexts.secondary = colors.secondary;
              }
              if (colors.background) {
                form.colors.background = colors.background;
                colorTexts.background = colors.background;
              }
              if (colors.text) {
                form.colors.text = colors.text;
                colorTexts.text = colors.text;
              }
              await nextTick();
            }

            if (Array.isArray(data.config.cskItems)) {
              applyCskItems(data.config.cskItems);
            } else {
              cskItems.value = [];
            }

            addStatus('Widget configuration loaded from server', 'success');
          }
        } else {
          const errorData = await response.json().catch(() => null);
          const errorMessage = errorData?.msg_text || errorData?.error || 'Invalid creation token or server error';
          cskError.value = errorMessage;
          cskItems.value = [];
          form.widgetInstanceId = null;
          addStatus(errorMessage, 'error');
        }
      } catch (error: any) {
        const message = (error as Error).message;
        cskError.value = message;
        cskItems.value = [];
        form.widgetInstanceId = null;
        addStatus('Failed to load configuration: ' + message, 'error');
      } finally {
        cskLoading.value = false;
      }
    };

    // Сохранение только CSK (bulk)
    const saveAllCsk = async () => {
      if (!form.creationToken) {
        addStatus('Creation token is required to save CSK.', 'error');
        return;
      }
      bulkSaving.value = true;
      try {
        const serverUrl = process.env.VUE_APP_SERVER_LINK || 'http://localhost:3001';
        const payload = {
          // сохраняем только cskItems, бэкенд может частично мерджить
          cskItems: cskItems.value.map(i => ({
            id: i.id,
            externalId: i.externalId,
            sortOrder: i.localSortOrder,
            isHidden: i.localHidden
          }))
        };
        const res = await fetch(serverUrl + '/widget/creation-config/' + form.creationToken, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.msg_text || err?.error || 'Failed to save CSK');
        }
        // фиксим оригинальные значения после успешного сохранения
        cskItems.value.forEach(i => {
          i.originalSortOrder = i.localSortOrder;
          i.originalHidden = i.localHidden;
          i.sortOrder = i.localSortOrder;
          i.isHidden = i.localHidden;
          i.saveMessage = 'Saved';
          i.saveError = '';
        });
        addStatus('All CSK changes saved.', 'success');
      } catch (e: any) {
        addStatus(e.message || String(e), 'error');
      } finally {
        bulkSaving.value = false;
      }
    };

    // Сохранение одной записи CSK
    const saveSingleCsk = async (item: CskEntry) => {
      if (!form.creationToken) {
        item.saveError = 'Creation token required';
        return;
      }
      item.saving = true;
      item.saveError = '';
      item.saveMessage = '';
      try {
        const serverUrl = process.env.VUE_APP_SERVER_LINK || 'http://localhost:3001';
        // используем тот же PUT (частичное обновление списка)
        const payload = {
          cskItems: [
            {
              id: item.id,
              externalId: item.externalId,
              sortOrder: item.localSortOrder,
              isHidden: item.localHidden
            }
          ]
        };
        const res = await fetch(serverUrl + '/widget/creation-config/' + form.creationToken, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.msg_text || err?.error || 'Failed to save CSK entry');
        }
        item.originalSortOrder = item.localSortOrder;
        item.originalHidden = item.localHidden;
        item.sortOrder = item.localSortOrder;
        item.isHidden = item.localHidden;
        item.saveMessage = 'Saved';
      } catch (e: any) {
        item.saveError = e.message || String(e);
      } finally {
        item.saving = false;
      }
    };

    const generateWidget = async () => {
      if (!(await validateForm())) {
        return;
      }

      // Save configuration to server
      try {
        const serverUrl = process.env.VUE_APP_SERVER_LINK || 'http://localhost:3001';
                 const updateResponse = await fetch(serverUrl + '/widget/creation-config/' + form.creationToken, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
                     body: JSON.stringify({
             displayType: form.displayType,
             language: form.language,
             callbackUrl: form.callbackUrl || null,
             sendUnsignedFiles: form.sendUnsignedFiles,
             showFileUpload: form.showFileUpload,
             embeddingBlock: form.embeddingBlock || null,
             colors: { ...form.colors },
             enableSessionPersistence: form.enableSessionPersistence,
             fileLimit: form.fileLimit,
             requireClientKey: form.requireClientKey
           })
        });

        if (!updateResponse.ok) {
          addStatus('Failed to save configuration to server', 'error');
          return;
        }

        const config = {
          apiKey: form.usageToken,
          displayType: form.displayType,
          embeddingBlock: form.embeddingBlock,
          language: form.language,
          callbackUrl: form.callbackUrl || null,
          sendUnsignedFiles: form.sendUnsignedFiles,
          showFileUpload: form.showFileUpload,
          enableSessionPersistence: form.enableSessionPersistence,
          fileLimit: form.fileLimit,
          requireClientKey: form.requireClientKey,
          colors: { ...form.colors }
        };

        generatedConfig.value = config;

        // Generate code
        generateCode(config);

        // Update preview
        updatePreview(config);

        addStatus('Widget configuration saved and generated successfully!', 'success');

        // Switch to preview tab
        activeTab.value = 'preview';
      } catch (error) {
        addStatus('Failed to save configuration: ' + (error as Error).message, 'error');
      }
    };

    // Helper function to escape HTML
    const escapeHtml = (text: string) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };

         const generateCode = (config: any) => {
       const serverUrl = process.env.VUE_APP_SERVER_LINK || 'http://localhost:3001';
       
       // Create a compact, obfuscated widget code similar to major providers
       let code = '';
       
       // Define script tags as variables to avoid Vue parsing issues
       const scriptOpen = '<' + 'script';
       const scriptClose = '<' + '/script>';
       const scriptEnd = '>' + scriptClose;
       
                               // Generate the same code for both modal and inline - server will handle the type
                code = '<div id="' + (config.embeddingBlock || 'sign_widget_container') + '"></div>\n' +
                  scriptOpen + ' defer>\n' +
                  '(function(s,i,g,n,w,d,e,t){s["SignWidget"]=w;s[w]=s[w]||function(){(s[w].q=s[w].q||[]).push(arguments)};s[w].l=1*new Date();e=i.createElement(g);t=i.getElementsByTagName(g)[0];e.async=1;e.src=n;t.parentNode.insertBefore(e,t)})(window,document,"script","' + serverUrl + '/widget/sign-widget-loader.js","sw");\n' +
                  '// Auto-initialize when script is loaded\n' +
                  'var checkSw = setInterval(function() {\n' +
                  '    if (window.sw) {\n' +
                  '        clearInterval(checkSw);\n' +
                  '        sw("init",{"apiKey":"' + config.apiKey + '"});\n' +
                  '    }\n' +
                  '}, 100);\n' +
                  scriptClose;

               // Add usage examples
        code += '\n' +
          '<!-- Usage Examples -->\n' +
          '<!-- \n' +
          '1. Show modal widget:\n' +
          '   sw("show", "your-unique-uuid");\n' +
          '   \n' +
          '2. Create inline widget:\n' +
          '   sw("create", "your-unique-uuid");\n' +
          '   \n' +
          '3. Load files programmatically:\n' +
          '   sw("loadFiles", ["https://example.com/file.pdf"]);\n' +
          '   \n' +
          '4. Load client key for secure session access (if required):\n' +
          '   sw("loadClientKey", "your-client-key");\n' +
          '-->';

       // Update the generated code in the template
       generatedCodeText.value = '<pre><code>' + escapeHtml(code) + '</code></pre>';
     };

    const updatePreview = (config: any) => {
      if (!widgetPreview.value) return;

      widgetPreview.value.innerHTML = '';

      if (config.displayType === 'modal') {
        // For modal, show the actual generated code and a test button
        const codeContainer = document.createElement('div');
        codeContainer.style.marginBottom = '20px';
        codeContainer.innerHTML = '<h4>Generated Code Preview:</h4><pre><code>' + escapeHtml(generatedCodeText.value.replace('<pre><code>', '').replace('</code></pre>', '')) + '</code></pre>';
        widgetPreview.value.appendChild(codeContainer);
        
        const btn = document.createElement('button');
        btn.className = 'btn btn-primary';
        btn.textContent = 'Test Widget';
        btn.onclick = () => testWidget();
        widgetPreview.value.appendChild(btn);
      } else {
        // For inline, create the actual container and embed the widget
        const container = document.createElement('div');
        container.id = config.embeddingBlock || 'widget-preview';
        container.style.minHeight = '300px';
        container.style.border = '2px dashed #dee2e6';
        container.style.borderRadius = '8px';
        container.style.padding = '20px';
        container.style.textAlign = 'center';
        container.innerHTML = '<p>Widget will be embedded here</p>';
        widgetPreview.value.appendChild(container);
        
        // Add a test button below the container
        const btn = document.createElement('button');
        btn.className = 'btn btn-primary';
        btn.style.marginTop = '10px';
        btn.textContent = 'Test Widget';
        btn.onclick = () => testWidget();
        widgetPreview.value.appendChild(btn);
      }
    };

         const testWidget = async () => {
       if (!generatedConfig.value) {
         addStatus('Please generate widget configuration first', 'error');
         return;
       }

       try {
         const testUuid = 'test-uuid-' + Date.now();
         
         // Clear any existing widgets
         const existingWidgets = document.querySelectorAll('.sign-widget-modal, .sign-widget-inline');
         existingWidgets.forEach(widget => {
           widget.remove();
         });
         
         // Initialize the widget first
         await (window as any).sw('init', {
           apiKey: generatedConfig.value.apiKey,
           type: generatedConfig.value.displayType,
           ...(generatedConfig.value.displayType === 'inline' && { container: generatedConfig.value.embeddingBlock || 'sign_widget_container' })
         });
         
         if (generatedConfig.value.displayType === 'modal') {
           // Use the new compact API
           await (window as any).sw('show', testUuid);
           addStatus('Modal widget opened successfully!', 'success');
         } else {
           // Use the new compact API for inline widget
           await (window as any).sw('create', testUuid);
           addStatus('Inline widget created successfully!', 'success');
         }
         
         // If file upload is disabled, automatically load a sample file
         if (!generatedConfig.value.showFileUpload) {
           setTimeout(async () => {
             try {
               await (window as any).sw('loadFiles', ['https://ontheline.trincoll.edu/images/bookdown/sample-local-pdf.pdf']);
               addStatus('Sample file loaded automatically', 'info');
             } catch (error) {
               console.log('Could not load sample file:', error);
             }
           }, 1000); // Wait for widget to initialize
         }
       } catch (error) {
         addStatus('Error testing widget: ' + (error as Error).message, 'error');
       }
     };

    const copyCode = () => {
      if (!generatedCodeText.value || generatedCodeText.value.includes('Configure your widget')) {
        addStatus('No code generated yet. Please generate widget first.', 'error');
        return;
      }

      // Extract the code from the pre/code element
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = generatedCodeText.value;
      const codeElement = tempDiv.querySelector('code');
      if (!codeElement) return;

      // Get the actual code content (without HTML entities)
      const codeContent = codeElement.textContent || '';
      
      // Create a temporary textarea to copy
      const textArea = document.createElement('textarea');
      textArea.value = codeContent;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        addStatus('Code copied to clipboard!', 'success');
      } catch (err) {
        addStatus('Failed to copy code: ' + (err as Error).message, 'error');
      } finally {
        document.body.removeChild(textArea);
      }
    };

    const resetForm = () => {
      form.creationToken = '';
      form.usageToken = '';
      form.displayType = 'modal';
      form.embeddingBlock = '';
      form.language = 'en';
      form.callbackUrl = '';
      form.sendUnsignedFiles = false;
      form.fileLimit = null;
      form.showFileUpload = true;
      form.colors.primary = '#007bff';
      form.colors.secondary = '#6c757d';
      form.colors.background = '#ffffff';
      form.colors.text = '#212529';
      colorTexts.primary = '#007bff';
      colorTexts.secondary = '#6c757d';
      colorTexts.background = '#ffffff';
             colorTexts.text = '#212529';

       generatedConfig.value = null;
       generatedCodeText.value = '// Configure your widget and click "Generate Widget" to see the code here.';
       if (widgetPreview.value) {
         widgetPreview.value.innerHTML = '<p>Configure the widget and click "Generate Widget" to see a preview here.</p>';
       }

       addStatus('Form reset successfully!', 'info');
     };

     onMounted(() => {
       // Load widget script if not already loaded
       if (!(window as any).SignWidget) {
         const script = document.createElement('script');
         script.src = (process.env.VUE_APP_SERVER_LINK || 'http://localhost:3001') + '/widget/sign-widget-loader.js';
         document.head.appendChild(script);
       }
     });

     // Watch for changes in creation token and load configuration
     watch(() => form.creationToken, (newToken) => {
       if (newToken) {
         loadConfigFromCreationToken();
       }
     });

     return {
       activeTab,
       form,
       colorTexts,
       statusMessage,
       statusType,
       widgetPreview,
       generatedCode,
       generatedCodeText,
       addStatus,
       updateColorText,
       updateColorPicker,
       toggleEmbeddingBlock,
       validateForm,
       loadConfigFromCreationToken,
       generateWidget,
       generateCode,
       updatePreview,
       testWidget,
       copyCode,
       resetForm,
       escapeHtml,
       cskItems,
       cskItemsSorted,
       cskLoading,
       cskError,
       bulkSaving,
       saveAllCsk,
       saveSingleCsk,
       resetCskEntry,
       resetAllCsk,
     };
   }
};
